import { useLocalStorage } from '@vueuse/core';
import { nextTick } from 'vue';

import {
  isImpactfulQuizQuestion,
  OnsiteOfferQuizConfig,
  questions,
  QuizResults,
} from '@/data/quiz/onsiteOffer';
import { setCookie } from '@/utils/isomorphic/cookie';

export function useOnsiteOffer() {
  const config = useLocalStorage<OnsiteOfferQuizConfig>('onsiteOfferQuiz.config', {
    isOpen: false,
  });

  const results = useLocalStorage<QuizResults>('onsiteOfferQuiz.results', () => {
    const [firstQuestion, secondQuestion] = questions.filter(isImpactfulQuizQuestion);
    return {
      selections: [firstQuestion, secondQuestion],
    };
  });

  function closeModal() {
    config.value.isOpen = false;
  }

  function openOnsiteOffer(initialScreen: OnsiteOfferQuizConfig['initialScreen']) {
    config.value.initialScreen = initialScreen;
    config.value.isOpen = true;
  }

  function rescindOffer() {
    closeModal();

    setCookie('dy_onsite_offer_quiz_v3_success', 'true');

    if (config.value.initialScreen?.stickyBubbleSelector) {
      document
        .querySelector(config.value.initialScreen.stickyBubbleSelector)
        ?.classList.add('hidden');
    }
  }

  /**
   * This is just a helper method that wraps `nextTick` to make the code self documenting at the call sites.
   *
   * The reason we need to call nextTick at the call sites is because after updating `results`, we generally emit
   * an event that leads to the destruction of the component and something related to that timing was keeping storage
   * from being updated.  `useLocalStorage` watches for changes on the reactive property, but it wasn't hearing the
   * change. Using `nextTick` fixes this.
   */
  async function waitForStorageUpdate() {
    await nextTick();
  }

  return {
    closeModal,
    config,
    openOnsiteOffer,
    rescindOffer,
    results,
    waitForStorageUpdate,
  };
}
